import React, { useRef, useState, useMemo } from 'react';
import styled from 'styled-components';
import { v4 as uuidv4 } from 'uuid';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { cleanupLinks } from '../../../../Components/TextEditor/Utils';
import { PROFIL_CREATEUR_OPTIONS } from '../sdeConstants';
import { uploadSDEFile } from '../sdeSupabaseService';

const StepContainer = styled.div`max-width: 700px; margin: 0 auto;`;
const StepTitle = styled.h2`font-size: 1.5rem; font-weight: 600; color: #1F2937; margin-bottom: 0.5rem;`;
const StepSubtitle = styled.p`color: #6B7280; margin-bottom: 1.25rem; font-size: 0.95rem;`;
const FormGroup = styled.div`margin-bottom: 0.75rem;`;
const Label = styled.label`
  display: block; font-size: 0.95rem; font-weight: 500; color: #374151; margin-bottom: 0.4rem;
  ${props => props.required && `&::after { content: ' *'; color: #EF4444; }`}
`;
const Hint = styled.p`font-size: 0.85rem; color: #9CA3AF; margin: 0.25rem 0 0.5rem;`;
const Input = styled.input`
  width: 100%; padding: 0.75rem 1rem; font-size: 0.95rem; border: 2px solid #E5E7EB; border-radius: 8px;
  background: #F9FAFB; &:focus { outline: none; border-color: #2563EB; background: white; }
`;
const Textarea = styled.textarea`
  width: 100%; padding: 0.75rem 1rem; font-size: 0.95rem; border: 2px solid #E5E7EB; border-radius: 8px;
  background: #F9FAFB; min-height: 80px; resize: vertical;
  &:focus { outline: none; border-color: #2563EB; }
`;

const ProjectCard = styled.div`
  background: white; border: 1px solid #E5E7EB; border-radius: 10px; padding: 1rem 1.25rem; margin-bottom: 0.75rem;
  position: relative;
`;
const ProjectHeader = styled.div`
  display: flex; justify-content: space-between; align-items: center; margin-bottom: 0.5rem;
  h4 { margin: 0; font-size: 1rem; color: #374151; }
`;
const RemoveBtn = styled.button`
  background: #FEE2E2; color: #DC2626; border: none; padding: 0.4rem 0.8rem; border-radius: 6px;
  font-size: 0.85rem; cursor: pointer; font-weight: 500;
  &:hover { background: #FECACA; }
`;
const AddButton = styled.button`
  width: 100%; padding: 1rem; background: #F0F7FF; border: 2px dashed #93C5FD; border-radius: 12px;
  color: #2563EB; font-size: 1rem; font-weight: 600; cursor: pointer; transition: all 0.2s;
  &:hover { background: #DBEAFE; border-color: #2563EB; }
`;
const EmptyState = styled.div`
  text-align: center; padding: 2rem; color: #9CA3AF;
  p { margin: 0.5rem 0; }
`;
const CheckboxGroup = styled.div`
  display: flex; flex-direction: column; gap: 2px;
`;
const CheckboxLabel = styled.label`
  display: flex; align-items: center; gap: 8px; font-size: 0.85rem; color: #374151;
  cursor: pointer; padding: 2px 6px; border-radius: 4px; transition: background 0.15s;
  margin: 0; line-height: 1.2;
  &:hover { background: #F3F4F6; }
  input[type="checkbox"] { width: 16px; height: 16px; accent-color: #2563EB; cursor: pointer; margin: 0; }
`;
const SectionDivider = styled.div`
  margin: 1.25rem 0 1rem; padding-top: 1rem; border-top: 1px solid #E5E7EB;
  h3 { font-size: 1.1rem; font-weight: 600; color: #374151; margin: 0 0 0.25rem; }
  p { color: #9CA3AF; font-size: 0.85rem; margin: 0; }
`;
const UploadZone = styled.div`
  border: 2px dashed #D1D5DB; border-radius: 8px; padding: 1rem; text-align: center;
  cursor: pointer; transition: all 0.2s; background: #FAFAFA;
  &:hover { border-color: #93C5FD; background: #F0F7FF; }
  p { color: #6B7280; margin: 0.25rem 0; font-size: 0.85rem; }
`;
const FilePreview = styled.div`
  display: flex; flex-wrap: wrap; gap: 0.5rem; margin-top: 0.5rem;
`;
const FileThumb = styled.div`
  position: relative; width: 80px; height: 80px; border-radius: 6px; overflow: hidden;
  border: 1px solid #E5E7EB;
  img { width: 100%; height: 100%; object-fit: cover; }
  button {
    position: absolute; top: 2px; right: 2px; background: rgba(239,68,68,0.9); color: white;
    border: none; border-radius: 50%; width: 20px; height: 20px; cursor: pointer;
    font-size: 0.65rem; display: flex; align-items: center; justify-content: center;
  }
`;

const CommentsEditor = styled.div`
  .ql-toolbar { border: 2px solid #E5E7EB; border-bottom: 1px solid #E5E7EB; border-radius: 8px 8px 0 0; background: #F9FAFB; }
  .ql-container { border: 2px solid #E5E7EB; border-top: none; border-radius: 0 0 8px 8px; background: #F9FAFB; min-height: 80px; font-size: 0.95rem; }
  .ql-container:focus-within { border-color: #2563EB; background: white; }
  .ql-toolbar:has(+ .ql-container:focus-within) { border-color: #2563EB; background: white; }
  .ql-editor { min-height: 80px; }
  .ql-editor a { color: #2563EB; text-decoration: underline; }
  .ql-editor.ql-blank::before { color: #9CA3AF; font-style: normal; }
`;

const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB per file
const ACCEPTED_FORMATS = '.jpg,.jpeg,.png,.webp,.pdf,.mp4,.mov,image/jpeg,image/png,image/webp,application/pdf,video/mp4,video/quicktime';

const PopAStep3_Projects = ({ data, updateData, lang = 'fr', eventId }) => {
  const projects = data.projects || [];
  const metadata = data.metadata || {};
  const fileInputRefs = useRef({});
  const [uploadingProject, setUploadingProject] = useState(null);
  const commentsModules = useMemo(() => ({ toolbar: [['link']] }), []);

  const addProject = () => {
    updateData({
      projects: [...projects, {
        id: uuidv4(),
        description: '',
        typologie: '',
        savoir_faire: '',
        profil_createur: [],
        profil_precisions: '',
        files: [],
      }]
    });
  };

  const updateProject = (id, field, value) => {
    updateData({
      projects: projects.map(p => p.id === id ? { ...p, [field]: value } : p)
    });
  };

  const toggleProfilCreateur = (projectId, value) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    const current = project.profil_createur || [];
    const next = current.includes(value)
      ? current.filter(v => v !== value)
      : [...current, value];
    updateProject(projectId, 'profil_createur', next);
  };

  const removeProject = (id) => {
    updateData({ projects: projects.filter(p => p.id !== id) });
  };

  const handleFileUpload = async (projectId, files) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;

    setUploadingProject(projectId);
    const newFiles = [...(project.files || [])];
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        alert(lang === 'fr'
          ? `Le fichier "${file.name}" dépasse la limite de 50 Mo.`
          : `File "${file.name}" exceeds the 50MB limit.`);
        continue;
      }
      const safeName = file.name.normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/[^a-zA-Z0-9._-]/g, '_');
      const path = `portfolio/${eventId || 'draft'}/${projectId}/${Date.now()}-${safeName}`;
      const result = await uploadSDEFile(file, path);
      if (result.error) {
        console.error('Upload error:', result.error);
        alert(lang === 'fr'
          ? `Erreur lors de l'envoi de "${file.name}": ${result.error.message || 'Erreur inconnue'}`
          : `Error uploading "${file.name}": ${result.error.message || 'Unknown error'}`);
        continue;
      }
      if (result.data) {
        newFiles.push({
          path: result.data.path,
          publicUrl: result.data.publicUrl,
          name: file.name,
          mimetype: file.type,
          size: file.size,
        });
      }
    }
    setUploadingProject(null);
    updateProject(projectId, 'files', newFiles);
  };

  const removeFile = (projectId, fileIndex) => {
    const project = projects.find(p => p.id === projectId);
    if (!project) return;
    updateProject(projectId, 'files', project.files.filter((_, i) => i !== fileIndex));
  };

  return (
    <StepContainer>
      <StepTitle>{lang === 'fr' ? 'Besoins / Projets en cours ou à venir' : 'Needs / Current or upcoming projects'}</StepTitle>
      <StepSubtitle>
        {lang === 'fr'
          ? 'Décrivez les projets pour lesquels vous recherchez un designer'
          : 'Describe the projects for which you are looking for a designer'}
      </StepSubtitle>

      {projects.length === 0 ? (
        <EmptyState>
          <p style={{ fontSize: '2rem' }}>📋</p>
          <p>{lang === 'fr' ? 'Aucun projet ajouté' : 'No projects added yet'}</p>
          <p style={{ fontSize: '0.85rem' }}>{lang === 'fr' ? 'Cliquez ci-dessous pour ajouter un projet' : 'Click below to add a project'}</p>
        </EmptyState>
      ) : (
        projects.map((project, index) => (
          <ProjectCard key={project.id}>
            <ProjectHeader>
              <h4>{lang === 'fr' ? `Projet ${index + 1}` : `Project ${index + 1}`}</h4>
              <RemoveBtn onClick={() => removeProject(project.id)}>
                {lang === 'fr' ? 'Supprimer' : 'Remove'}
              </RemoveBtn>
            </ProjectHeader>

            <FormGroup>
              <Label required>{lang === 'fr' ? 'Description du projet' : 'Project description'}</Label>
              <Hint>
                {lang === 'fr'
                  ? 'Décrivez votre projet, vos besoins et vos attentes vis-à-vis du designer'
                  : 'Describe your project, needs and expectations from the designer'}
              </Hint>
              <Textarea
                value={project.description || ''}
                onChange={e => updateProject(project.id, 'description', e.target.value)}
                placeholder={lang === 'fr'
                  ? 'Ex: Développement d\'une nouvelle collection de mobilier de bureau...'
                  : 'E.g., Development of a new office furniture collection...'}
              />
            </FormGroup>

            <FormGroup>
              <Label>{lang === 'fr' ? 'Typologie' : 'Typology'}</Label>
              <Input
                value={project.typologie || ''}
                onChange={e => updateProject(project.id, 'typologie', e.target.value)}
                placeholder={lang === 'fr' ? 'Ex: Assise, luminaire, mobilier outdoor...' : 'E.g., Seating, lighting, outdoor furniture...'}
              />
            </FormGroup>

            <FormGroup>
              <Label>{lang === 'fr' ? 'Savoir-faire recherché' : 'Expertise sought'}</Label>
              <Input
                value={project.savoir_faire || ''}
                onChange={e => updateProject(project.id, 'savoir_faire', e.target.value)}
                placeholder={lang === 'fr' ? 'Ex: Travail du bois, tapisserie...' : 'E.g., Woodwork, upholstery...'}
              />
            </FormGroup>

            <FormGroup>
              <Label required>{lang === 'fr' ? 'Profil du créateur recherché' : 'Designer profile sought'}</Label>
              <CheckboxGroup>
                {PROFIL_CREATEUR_OPTIONS.map(opt => (
                  <CheckboxLabel key={opt.value}>
                    <input
                      type="checkbox"
                      checked={(project.profil_createur || []).includes(opt.value)}
                      onChange={() => toggleProfilCreateur(project.id, opt.value)}
                    />
                    {lang === 'fr' ? opt.label_fr : opt.label_en}
                  </CheckboxLabel>
                ))}
              </CheckboxGroup>
            </FormGroup>

            <FormGroup>
              <Label>{lang === 'fr' ? 'Précisions sur le profil' : 'Profile details'}</Label>
              <Input
                value={project.profil_precisions || ''}
                onChange={e => updateProject(project.id, 'profil_precisions', e.target.value)}
                placeholder={lang === 'fr' ? 'Précisions complémentaires...' : 'Additional details...'}
              />
            </FormGroup>

            <FormGroup>
              <Label>{lang === 'fr' ? 'Visuels / Documents' : 'Visuals / Documents'}</Label>
              <UploadZone onClick={() => !uploadingProject && fileInputRefs.current[project.id]?.click()}>
                {uploadingProject === project.id ? (
                  <p style={{ color: '#2563EB', fontWeight: 500 }}>{lang === 'fr' ? 'Envoi en cours...' : 'Uploading...'}</p>
                ) : (
                  <>
                    <p style={{ fontSize: '1.2rem', margin: 0 }}>📷</p>
                    <p>{lang === 'fr' ? 'Cliquez pour ajouter des fichiers' : 'Click to add files'}</p>
                    <p style={{ fontSize: '0.75rem', color: '#9CA3AF' }}>JPG, PNG, PDF, MP4, MOV — max 50 Mo/fichier</p>
                  </>
                )}
                <input
                  ref={el => fileInputRefs.current[project.id] = el}
                  type="file" accept={ACCEPTED_FORMATS} multiple style={{ display: 'none' }}
                  onChange={e => { handleFileUpload(project.id, Array.from(e.target.files)); e.target.value = ''; }}
                />
              </UploadZone>
              {project.files?.length > 0 && (
                <FilePreview>
                  {project.files.map((file, fi) => (
                    <FileThumb key={fi}>
                      {file.mimetype?.startsWith('image/') ? (
                        <img src={file.publicUrl} alt={file.name} />
                      ) : file.mimetype?.startsWith('video/') ? (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#F3F4F6', fontSize: '1.2rem' }}>🎬</div>
                      ) : (
                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: '#F3F4F6', fontSize: '0.6rem', padding: '0.4rem', textAlign: 'center', color: '#6B7280' }}>{file.name}</div>
                      )}
                      <button type="button" onClick={(e) => { e.stopPropagation(); removeFile(project.id, fi); }}>&times;</button>
                    </FileThumb>
                  ))}
                </FilePreview>
              )}
            </FormGroup>
          </ProjectCard>
        ))
      )}

      <AddButton onClick={addProject}>
        + {lang === 'fr' ? 'Ajouter un projet' : 'Add a project'}
      </AddButton>

      <SectionDivider>
        <h3>{lang === 'fr' ? 'Commentaires, précisions' : 'Comments, details'}</h3>
        <p>{lang === 'fr' ? 'Informations complémentaires sur vos attentes' : 'Additional information about your expectations'}</p>
      </SectionDivider>

      <FormGroup>
        <CommentsEditor>
          <ReactQuill
            theme="snow"
            value={metadata.comments || ''}
            onChange={val => updateData({ metadata: { ...data.metadata, comments: cleanupLinks(val) } })}
            modules={commentsModules}
            placeholder={lang === 'fr'
              ? 'Commentaires ou précisions supplémentaires...'
              : 'Additional comments or details...'}
          />
        </CommentsEditor>
      </FormGroup>
    </StepContainer>
  );
};

export default PopAStep3_Projects;
